// Sentry ErrorBoundary fallback component
// Moved to separate file to maintain React Fast Refresh compliance
import { i18n, type MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { AVAILABLE_LOCALES, DEFAULT_LOCALE, getInitialLocale, type Locale } from '@/lib/i18n';

interface SentryFallbackProps {
  error: unknown;
  resetError: () => void;
}

type FallbackKey = 'title' | 'description' | 'retry' | 'reload';

const descriptors: Record<FallbackKey, MessageDescriptor> = {
  title: msg({ id: 'errorBoundary.app.title', message: 'Something went wrong' }),
  description: msg({
    id: 'errorBoundary.app.description',
    message: 'An unexpected error occurred. Try again or reload the page.',
  }),
  retry: msg({ id: 'common.retry', message: 'Try again' }),
  reload: msg({ id: 'errorBoundary.reload', message: 'Reload page' }),
};

// This boundary sits above the I18nProvider, so it can render before any
// catalog is loaded (a crash during start-up) or after loading failed. Then
// these copies of the catalog strings speak the user's language instead.
const builtIn: Record<Locale, Record<FallbackKey, string>> = {
  en: {
    title: 'Something went wrong',
    description: 'An unexpected error occurred. Try again or reload the page.',
    retry: 'Try again',
    reload: 'Reload page',
  },
  de: {
    title: 'Etwas ist schiefgelaufen',
    description: 'Ein unerwarteter Fehler ist aufgetreten. Versuchen Sie es erneut oder laden Sie die Seite neu.',
    retry: 'Erneut versuchen',
    reload: 'Seite neu laden',
  },
  fr: {
    title: "Une erreur s'est produite",
    description: "Une erreur inattendue s'est produite. Réessayez ou rechargez la page.",
    retry: 'Réessayer',
    reload: 'Recharger la page',
  },
  it: {
    title: 'Si è verificato un errore',
    description: 'Si è verificato un errore imprevisto. Riprovare o ricaricare la pagina.',
    retry: 'Riprova',
    reload: 'Ricarica la pagina',
  },
  pt: {
    title: 'Ocorreu um erro',
    description: 'Ocorreu um erro inesperado. Tente novamente ou recarregue a página.',
    retry: 'Tentar novamente',
    reload: 'Recarregar a página',
  },
  es: {
    title: 'Se ha producido un error',
    description: 'Se ha producido un error inesperado. Vuelva a intentarlo o recargue la página.',
    retry: 'Reintentar',
    reload: 'Recargar la página',
  },
};

/** The stored or browser locale; storage can throw (private mode, blocked site data). */
function fallbackLocale(): Locale {
  try {
    return getInitialLocale();
  } catch {
    const lang = typeof navigator !== 'undefined' ? navigator.language.split('-')[0] : '';
    return (AVAILABLE_LOCALES as readonly string[]).includes(lang) ? (lang as Locale) : DEFAULT_LOCALE;
  }
}

function fallbackText(): { lang: string; text: (key: FallbackKey) => string } {
  const catalogReady = Boolean(i18n.locale) && descriptors.title.id in (i18n.messages ?? {});
  if (catalogReady) {
    return { lang: i18n.locale, text: (key) => i18n._(descriptors[key]) };
  }
  const locale = fallbackLocale();
  return { lang: locale, text: (key) => builtIn[locale][key] };
}

export function SentryFallback({ error, resetError }: SentryFallbackProps) {
  // Never render the raw error text to users — it can carry internal details
  // or PII (security review P21). The error is already reported to Sentry by
  // the boundary; surface it to the console for developers only.
  if (import.meta.env.DEV) {
    console.error("Application error boundary:", error);
  }
  const { lang, text } = fallbackText();

  return (
    <div lang={lang} className="flex h-screen w-screen items-center justify-center bg-background">
      <div className="max-w-md px-6 text-center" role="alert" aria-live="assertive">
        <div className="mb-6">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-8 w-8 text-destructive" aria-hidden="true" />
          </div>
          <h2 className="mb-2 text-xl font-semibold text-foreground">{text('title')}</h2>
          <p className="mb-6 text-sm text-muted-foreground">{text('description')}</p>
        </div>
        <div className="flex flex-col gap-3">
          <Button type="button" onClick={resetError}>
            {text('retry')}
          </Button>
          <Button type="button" variant="secondary" onClick={() => window.location.reload()}>
            {text('reload')}
          </Button>
        </div>
      </div>
    </div>
  );
}
